import React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Heading,
  List,
  Link,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageUpload,
  Table,
  TableToolbar,
  Undo
} from 'ckeditor5';
import type { EditorConfig } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { AppUploadAdapterPlugin } from './AppUploadAdapter';
import './RichTextEditor.css';

export const RICH_TEXT_TOOLBAR = [
  'undo',
  'redo',
  '|',
  'heading',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'link',
  'uploadImage',
  'insertTable'
];

export interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}

export function isEmptyRichText(html: string | undefined): boolean {
  if (!html) {
    return true;
  }
  if (/<img\b/i.test(html) || /<table\b/i.test(html)) {
    return false;
  }
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return text.length === 0;
}

const LICENSE_KEY = process.env.REACT_APP_CKEDITOR_LICENSE_KEY || 'GPL';

export const richTextEditorConfig = {
  licenseKey: LICENSE_KEY,
  plugins: [
    Essentials,
    Paragraph,
    Bold,
    Italic,
    Underline,
    Heading,
    List,
    Link,
    Image,
    ImageToolbar,
    ImageCaption,
    ImageStyle,
    ImageUpload,
    Table,
    TableToolbar,
    Undo
  ],
  extraPlugins: [AppUploadAdapterPlugin],
  toolbar: {
    items: RICH_TEXT_TOOLBAR
  },
  heading: {
    options: [
      { model: 'paragraph' as const, title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading2' as const, view: 'h2' as const, title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3' as const, view: 'h3' as const, title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4' as const, view: 'h4' as const, title: 'Heading 4', class: 'ck-heading_heading4' }
    ]
  },
  image: {
    toolbar: ['imageTextAlternative', 'toggleImageCaption', 'imageStyle:inline', 'imageStyle:block']
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
  }
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Enter details...',
  id
}) => {
  return (
    <div className="rich-text-editor" id={id}>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        disabled={disabled}
        config={{
          ...richTextEditorConfig,
          placeholder
        } satisfies EditorConfig}
        onChange={(_event, editor) => {
          onChange(editor.getData());
        }}
      />
    </div>
  );
};

export default RichTextEditor;
